import { useEffect, useCallback, useRef, useState } from 'react';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import { useQuery } from 'convex/react';
import { useTranslation } from 'react-i18next';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import TextAlign from '@tiptap/extension-text-align';
import { TextDirection } from 'tiptap-text-direction';
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import useFileStore from '../../stores/fileStore';
import useAppStore from '../../stores/appStore';
import useAIStore from '../../stores/aiStore';
import usePendingChangesStore from '../../stores/pendingChangesStore';
import convex from '../../convexClient';
import { api } from '../../../convex/_generated/api';
import { countWords } from '../../utils/markdown';
import { formatAuthorBotMemory } from '../../utils/authorBotMemory';
import { compareProjectFilesByOrder } from '../../../shared/projectFileOrder';
import { selectRecentChapterFiles } from '../../../shared/recentChapterContext';
import { buildStoryBiblePromptContext } from '../../../shared/storyBiblePrompt';
import {
    CHALLENGE_PROJECT_DRAFT_PATH,
    CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH,
    CHALLENGE_PROJECT_SUBMISSION_PATH,
} from '../../../shared/challengeProjectSeed';
import './RichTextEditor.css';

// Convert markdown to HTML for initial load
function markdownToHtml(md) {
    if (!md) return '';

    return md
        // Headers
        .replace(/^### (.*$)/gim, '<h3>$1</h3>')
        .replace(/^## (.*$)/gim, '<h2>$1</h2>')
        .replace(/^# (.*$)/gim, '<h1>$1</h1>')
        // Bold and italic
        .replace(/\*\*\*(.*?)\*\*\*/gim, '<strong><em>$1</em></strong>')
        .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/gim, '<em>$1</em>')
        // Links
        .replace(/\[(.*?)\]\((.*?)\)/gim, '<a href="$2">$1</a>')
        // Code blocks
        .replace(/```([\s\S]*?)```/gim, '<pre><code>$1</code></pre>')
        .replace(/`(.*?)`/gim, '<code>$1</code>')
        // Horizontal rules
        .replace(/^---$/gim, '<hr>')
        // Unordered lists
        .replace(/^\s*[-*+] (.*$)/gim, '<li>$1</li>')
        // Ordered lists
        .replace(/^\s*\d+\. (.*$)/gim, '<li>$1</li>')
        // Line breaks
        .replace(/\n\n/gim, '</p><p>')
        .replace(/\n/gim, '<br>');
}

// Convert HTML back to markdown for storage
function htmlToMarkdown(html) {
    if (!html) return '';

    const div = document.createElement('div');
    div.innerHTML = html;

    function processNode(node) {
        if (node.nodeType === Node.TEXT_NODE) {
            return node.textContent;
        }

        const tag = node.tagName?.toLowerCase();
        const children = Array.from(node.childNodes).map(processNode).join('');

        switch (tag) {
            case 'h1': return `# ${children}\n\n`;
            case 'h2': return `## ${children}\n\n`;
            case 'h3': return `### ${children}\n\n`;
            case 'strong':
            case 'b': return `**${children}**`;
            case 'em':
            case 'i': return `*${children}*`;
            case 'a': return `[${children}](${node.getAttribute('href') || ''})`;
            case 'code': return `\`${children}\``;
            case 'pre': return `\`\`\`\n${children}\n\`\`\`\n\n`;
            case 'hr': return '---\n\n';
            case 'li': return `- ${children}\n`;
            case 'ul':
            case 'ol': return `${children}\n`;
            case 'p': return `${children}\n\n`;
            case 'br': return '\n';
            default: return children;
        }
    }

    return processNode(div).trim();
}

// ── Search highlight plugin (module-level, created once) ──
const searchPluginKey = new PluginKey('search-highlight');

const SearchHighlight = Extension.create({
    name: 'searchHighlight',
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: searchPluginKey,
                state: {
                    init() { return { query: '', currentIndex: 0 }; },
                    apply(tr, prev) {
                        const meta = tr.getMeta(searchPluginKey);
                        return meta ? { query: meta.query, currentIndex: meta.currentIndex } : prev;
                    },
                },
                props: {
                    decorations(state) {
                        const pluginState = searchPluginKey.getState(state) || { query: '', currentIndex: 0 };
                        const { query, currentIndex } = pluginState;
                        if (!query) return DecorationSet.empty;
                        const results = findInDoc(state.doc, query);
                        if (results.length === 0) return DecorationSet.empty;
                        const safeIdx = ((currentIndex % results.length) + results.length) % results.length;
                        const decos = results.map((r, i) =>
                            Decoration.inline(r.from, r.to, {
                                class: i === safeIdx ? 'search-match search-match--active' : 'search-match',
                            })
                        );
                        return DecorationSet.create(state.doc, decos);
                    },
                },
            }),
        ];
    },
});

// ── Search helpers ──

function findInDoc(doc, query) {
    if (!query) return [];
    const results = [];
    const lower = query.toLowerCase();
    doc.descendants((node, pos) => {
        if (!node.isText) return;
        const text = node.text.toLowerCase();
        let idx = text.indexOf(lower);
        while (idx !== -1) {
            results.push({ from: pos + idx, to: pos + idx + query.length });
            idx = text.indexOf(lower, idx + 1);
        }
    });
    return results;
}

function FindBar({ editor, onClose }) {
    const { t } = useTranslation();
    const [query, setQuery] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [count, setCount] = useState(0);
    const inputRef = useRef(null);
    const resultsRef = useRef([]);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const doSearch = useCallback((q, idx) => {
        if (!editor?.view?.state) return;
        const found = findInDoc(editor.state.doc, q);
        resultsRef.current = found;
        setCount(found.length);
        const safeIdx = found.length > 0 ? ((idx % found.length) + found.length) % found.length : 0;
        setCurrentIndex(safeIdx);
        // Update decorations via plugin
        editor.view.dispatch(editor.state.tr.setMeta(searchPluginKey, { query: q, currentIndex: safeIdx }));
        if (found.length > 0 && found[safeIdx]) {
            try {
                editor.commands.setTextSelection({ from: found[safeIdx].from, to: found[safeIdx].to });
                const dom = editor.view.domAtPos(found[safeIdx].from);
                if (dom?.node) {
                    const el = dom.node.nodeType === Node.TEXT_NODE ? dom.node.parentElement : dom.node;
                    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            } catch (_) { /* ignore stale position */ }
        }
    }, [editor]);

    const handleChange = (e) => {
        const q = e.target.value;
        setQuery(q);
        doSearch(q, 0);
    };

    const goNext = () => doSearch(query, currentIndex + 1);
    const goPrev = () => doSearch(query, currentIndex - 1);

    const handleClose = useCallback(() => {
        if (editor?.view?.state) {
            editor.view.dispatch(editor.state.tr.setMeta(searchPluginKey, { query: '', currentIndex: 0 }));
        }
        onClose();
    }, [editor, onClose]);

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') { handleClose(); return; }
        if (e.key === 'Enter') { e.shiftKey ? goPrev() : goNext(); e.preventDefault(); }
    };

    return (
        <div className="find-bar">
            <div className="find-bar-inner">
                <svg className="find-bar-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                <input
                    ref={inputRef}
                    className="find-bar-input"
                    type="text"
                    value={query}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder={t('editor.findBar.placeholder')}
                />
                {query && (
                    <span className="find-bar-count">
                        {count > 0 ? t('editor.findBar.resultCount', { current: currentIndex + 1, count }) : t('editor.findBar.noResults')}
                    </span>
                )}
                <button className="find-bar-btn" onClick={goPrev} disabled={count === 0} title={t('editor.findBar.previous')}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="18 15 12 9 6 15" /></svg>
                </button>
                <button className="find-bar-btn" onClick={goNext} disabled={count === 0} title={t('editor.findBar.next')}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
                </button>
                <button className="find-bar-btn" onClick={handleClose} title={t('editor.findBar.close')}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
            </div>
        </div>
    );
}

function RichTextEditor({ projectId, path }) {
    const { t } = useTranslation();
    const { getFileContent, updateFileContent, markUnsaved, markSaved, files } = useFileStore();
    const { addWordsWritten, getProjectRtl, setProjectRtl } = useAppStore();
    const { selectedPersona } = useAIStore();
    const { addPendingChange } = usePendingChangesStore();
    const project = useQuery(api.projects.getById, projectId ? { projectId } : 'skip');
    const storyBible = useQuery(api.storyBibles.getCurrent, projectId ? { projectId } : 'skip');
    const mentorMemory = useQuery(api.authorBotMemory.listByProject, projectId ? { projectId } : 'skip');
    const hasCanonicalChallengeSubmission = files.some((file) => file.type === 'file' && file.path === CHALLENGE_PROJECT_SUBMISSION_PATH);
    const orderedChapterFiles = files
        .filter((file) => {
            if (file.type !== 'file' || typeof file.content !== 'string' || file.content.trim().length === 0) return false;
            return (file.path.startsWith('Chapters/') && file.path !== CHALLENGE_PROJECT_DRAFT_PATH) ||
                (!hasCanonicalChallengeSubmission && file.path === CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH);
        })
        .sort(compareProjectFilesByOrder);
    const saveTimeoutRef = useRef(null);
    const previousWordCountRef = useRef(0);
    const initialContentRef = useRef('');
    const [contextMenu, setContextMenu] = useState(null);
    const [showFind, setShowFind] = useState(false);

    // Use persisted RTL preference for this project
    const isRtl = getProjectRtl(projectId);
    const setIsRtl = (value) => setProjectRtl(projectId, typeof value === 'function' ? value(isRtl) : value);

    // Load initial content
    const content = getFileContent(path);
    initialContentRef.current = content;
    previousWordCountRef.current = countWords(content);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
            }),
            Link.configure({
                openOnClick: false,
            }),
            Placeholder.configure({
                placeholder: t('editor.startWriting'),
            }),
            Typography,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            TextDirection.configure({
                types: ['heading', 'paragraph', 'listItem', 'blockquote'],
            }),
            SearchHighlight,
        ],
        content: markdownToHtml(content),
        editorProps: {
            attributes: {
                class: 'prose-editor',
            },
        },
        onUpdate: ({ editor }) => {
            markUnsaved(path);

            // Debounced auto-save
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }

            saveTimeoutRef.current = setTimeout(async () => {
                const markdown = htmlToMarkdown(editor.getHTML());
                await updateFileContent(projectId, path, markdown);
                markSaved(path);

                // Track words written
                const currentWordCount = countWords(markdown);
                const wordDiff = currentWordCount - previousWordCountRef.current;
                if (wordDiff > 0) {
                    addWordsWritten(wordDiff);
                }
                previousWordCountRef.current = currentWordCount;
            }, 1000);
        },
    });

    // Update content when path changes
    useEffect(() => {
        if (editor && content !== initialContentRef.current) {
            editor.commands.setContent(markdownToHtml(content));
            initialContentRef.current = content;
        }
    }, [path, content, editor]);

    useEffect(() => {
        if (!editor?.view?.dom) return;
        const direction = isRtl ? 'rtl' : 'ltr';
        editor.view.dom.setAttribute('dir', direction);
        editor.view.dom.setAttribute('data-editor-dir', direction);
    }, [editor, isRtl]);

    // Cleanup
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, []);

    // Cmd+F / Ctrl+F → open find bar
    useEffect(() => {
        const handleFindShortcut = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
                e.preventDefault();
                setShowFind(true);
            }
        };
        document.addEventListener('keydown', handleFindShortcut);
        return () => document.removeEventListener('keydown', handleFindShortcut);
    }, []);

    useEffect(() => {
        const handleClick = () => setContextMenu(null);
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    const handleContextMenu = (event) => {
        if (!editor) return;
        const { from, to } = editor.state.selection;
        if (from === to) return;
        const selectionText = editor.state.doc.textBetween(from, to, '\n');
        if (!selectionText.trim()) return;
        event.preventDefault();
        setContextMenu({
            x: event.clientX,
            y: event.clientY,
            text: selectionText,
        });
    };

    const handleCopy = async () => {
        if (!contextMenu?.text) return;
        await navigator.clipboard.writeText(contextMenu.text);
        setContextMenu(null);
    };

    const handleCut = async () => {
        if (!contextMenu?.text || !editor) return;
        await navigator.clipboard.writeText(contextMenu.text);
        editor.chain().focus().deleteSelection().run();
        setContextMenu(null);
    };

    const handlePaste = async () => {
        if (!editor) return;
        const clip = await navigator.clipboard.readText();
        if (clip) {
            editor.chain().focus().insertContent(clip).run();
        }
        setContextMenu(null);
    };

    const handleSelectAll = () => {
        if (!editor) return;
        editor.chain().focus().selectAll().run();
        setContextMenu(null);
    };

    const buildContext = () => {
        const activeContent = getFileContent(path) || '';
        return {
            projectName: project?.title,
            genre: project?.genre,
            synopsis: project?.synopsis,
            storyBibleContext: buildStoryBiblePromptContext(storyBible),
            storyBibleSummary: storyBible?.summary,
            authorBotMemory: formatAuthorBotMemory(mentorMemory || []),
            activeFile: {
                path,
                content: activeContent,
            },
            recentChapterFiles: selectRecentChapterFiles(orderedChapterFiles, path, {
                previousCount: 1,
                fallbackCount: 1,
            }).map((file) => ({
                path: file.path,
                content: file.content || '',
            })),
            allFiles: files
                .filter((file) => file.type === 'file')
                .map((file) => ({
                    path: file.path,
                })),
            mentionFiles: [],
            personaId: selectedPersona,
        };
    };

    const [isAiActionLoading, setIsAiActionLoading] = useState(false);

    const runAiTransform = async (actionLabel) => {
        if (!contextMenu?.text) return;
        const selection = contextMenu.text;
        setContextMenu(null);

        try {
            setIsAiActionLoading(true);
            const response = await convex.action(api.ai.sendMessage, {
                projectId,
                messages: [
                    {
                        role: 'user',
                        content: `Task: ${actionLabel}.\n\nSelected text:\n${selection}\n\nReturn only the revised text.`,
                    },
                ],
                context: buildContext(),
                mode: 'transform',
                actionLabel,
            });

            const revisedText = response?.content?.trim();
            if (!revisedText) return;
            const originalContent = getFileContent(path) || '';
            const updatedContent = originalContent.includes(selection)
                ? originalContent.replace(selection, revisedText)
                : originalContent;

            if (updatedContent !== originalContent) {
                addPendingChange(
                    path,
                    originalContent,
                    updatedContent,
                    `AI ${actionLabel.toLowerCase()} suggestion`
                );
            }
        } catch (error) {
            console.error('AI transform failed:', error);
        } finally {
            setIsAiActionLoading(false);
        }
    };

    const handleAddToChat = () => {
        if (!contextMenu?.text) return;
        window.dispatchEvent(new CustomEvent('ai:addToChat', { detail: contextMenu.text }));
        setContextMenu(null);
    };

    const handleInspire = () => {
        if (!contextMenu?.text) return;
        window.dispatchEvent(
            new CustomEvent('ai:addToChat', {
                detail: `Inspire me based on this excerpt:\n${contextMenu.text}`,
            })
        );
        setContextMenu(null);
    };

    const handleComment = () => {
        if (!contextMenu?.text) return;
        window.dispatchEvent(
            new CustomEvent('ai:addToChat', {
                detail: `Comment on this excerpt:\n${contextMenu.text}`,
            })
        );
        setContextMenu(null);
    };

    if (!editor) {
        return (
            <div className="editor-loading">
                <div className="spinner"></div>
                <span>{t('editor.loadingEditor')}</span>
            </div>
        );
    }

    return (
        <div className={`rich-text-editor ${isAiActionLoading ? 'ai-working' : ''}`}>
            <EditorToolbar editor={editor} onToggleFind={() => setShowFind((v) => !v)} isRtl={isRtl} onToggleRtl={() => setIsRtl((v) => !v)} />
            {showFind && <FindBar editor={editor} onClose={() => setShowFind(false)} />}

            {/* Floating Bubble Menu — appears on text selection */}
            {editor && (
                <BubbleMenu editor={editor} tippyOptions={{ duration: 150 }} className="bubble-toolbar">
                    <button
                        className={`toolbar-btn ${editor.isActive('heading', { level: 1 }) ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                        title={t('editor.heading1')}
                    >
                        H1
                    </button>
                    <button
                        className={`toolbar-btn ${editor.isActive('heading', { level: 2 }) ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                        title={t('editor.heading2')}
                    >
                        H2
                    </button>

                    <div className="toolbar-separator" />

                    <button
                        className={`toolbar-btn ${editor.isActive('bold') ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        title={t('editor.bold')}
                    >
                        <strong>B</strong>
                    </button>
                    <button
                        className={`toolbar-btn ${editor.isActive('italic') ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        title={t('editor.italic')}
                    >
                        <em>I</em>
                    </button>
                    <button
                        className={`toolbar-btn ${editor.isActive('strike') ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        title={t('editor.strikethrough')}
                    >
                        <s>S</s>
                    </button>

                    <div className="toolbar-separator" />

                    <button
                        className={`toolbar-btn ${editor.isActive('bulletList') ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        title={t('editor.bulletList')}
                    >
                        •
                    </button>
                    <button
                        className={`toolbar-btn ${editor.isActive('blockquote') ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleBlockquote().run()}
                        title={t('editor.quote')}
                    >
                        "
                    </button>
                    <button
                        className={`toolbar-btn ${editor.isActive('code') ? 'active' : ''}`}
                        onClick={() => editor.chain().focus().toggleCode().run()}
                        title={t('editor.inlineCode')}
                    >
                        {'</>'}
                    </button>
                </BubbleMenu>
            )}


            {/* Editor Content */}
            <div
                className={`editor-content-wrapper ${path === CHALLENGE_PROJECT_SUBMISSION_PATH || path === CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH ? 'challenge-submission-editor' : ''}`}
                dir={isRtl ? 'rtl' : 'ltr'}
                onContextMenu={handleContextMenu}
            >
                <EditorContent editor={editor} />
            </div>

            {contextMenu && (
                <div
                    className="editor-context-menu"
                    style={{ left: contextMenu.x, top: contextMenu.y }}
                >
                    {/* Edit actions */}
                    <button className="context-item" onClick={handleCut}>
                        <span className="ctx-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="6" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><line x1="20" y1="4" x2="8.12" y2="15.88" /><line x1="14.47" y1="14.48" x2="20" y2="20" /><line x1="8.12" y1="8.12" x2="12" y2="12" /></svg>
                        </span>
                        <span className="ctx-label">{t('editor.cut')}</span>
                        <span className="ctx-shortcut">⌘X</span>
                    </button>
                    <button className="context-item" onClick={handleCopy}>
                        <span className="ctx-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>
                        </span>
                        <span className="ctx-label">{t('editor.copy')}</span>
                        <span className="ctx-shortcut">⌘C</span>
                    </button>
                    <button className="context-item" onClick={handlePaste}>
                        <span className="ctx-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /></svg>
                        </span>
                        <span className="ctx-label">{t('editor.paste')}</span>
                        <span className="ctx-shortcut">⌘V</span>
                    </button>
                    <button className="context-item" onClick={handleSelectAll}>
                        <span className="ctx-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /></svg>
                        </span>
                        <span className="ctx-label">{t('editor.selectAll')}</span>
                        <span className="ctx-shortcut">⌘A</span>
                    </button>

                    <div className="context-divider" />

                    {/* AI actions */}
                    <div className="context-section">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /></svg>
                        {t('editor.aiActions')}
                    </div>
                    <button className="context-item" onClick={() => runAiTransform('Polish')}>
                        <span className="ctx-icon">✨</span>
                        <span className="ctx-label">{t('editor.polish')}</span>
                    </button>
                    <button className="context-item" onClick={() => runAiTransform('Expand')}>
                        <span className="ctx-icon">📐</span>
                        <span className="ctx-label">{t('editor.expand')}</span>
                    </button>
                    <button className="context-item" onClick={() => runAiTransform('Rewrite')}>
                        <span className="ctx-icon">🔄</span>
                        <span className="ctx-label">{t('editor.rewrite')}</span>
                    </button>
                    <button className="context-item" onClick={() => runAiTransform('Condense')}>
                        <span className="ctx-icon">🗜️</span>
                        <span className="ctx-label">{t('editor.condense')}</span>
                    </button>
                    <button className="context-item" onClick={handleComment}>
                        <span className="ctx-icon">💬</span>
                        <span className="ctx-label">{t('editor.comment')}</span>
                    </button>
                    <button className="context-item" onClick={handleInspire}>
                        <span className="ctx-icon">💡</span>
                        <span className="ctx-label">{t('editor.inspire')}</span>
                    </button>
                    <button className="context-item" onClick={handleAddToChat}>
                        <span className="ctx-icon">📨</span>
                        <span className="ctx-label">{t('editor.addToChat')}</span>
                    </button>

                    <div className="context-divider" />

                    {/* Team */}
                    <div className="context-section">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></svg>
                        {t('editor.team')}
                    </div>
                    <button className="context-item" onClick={handleComment}>
                        <span className="ctx-icon">📝</span>
                        <span className="ctx-label">{t('editor.addComment')}</span>
                    </button>
                    <button className="context-item" onClick={() => runAiTransform('Suggest Edit')}>
                        <span className="ctx-icon">✏️</span>
                        <span className="ctx-label">{t('editor.suggestEdit')}</span>
                    </button>
                </div>
            )
            }
        </div >
    );
}

const EditorToolbar = ({ editor, onToggleFind, isRtl, onToggleRtl }) => {
    const { t } = useTranslation();
    if (!editor) return null;

    return (
        <div className="editor-toolbar-sleek">
            <div className="toolbar-group">
                <button
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    className={`toolbar-btn ${editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}`}
                    title={t('editor.heading1')}
                >
                    H1
                </button>
                <button
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    className={`toolbar-btn ${editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}`}
                    title={t('editor.heading2')}
                >
                    H2
                </button>
            </div>

            <div className="toolbar-group">
                <button
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={`toolbar-btn ${editor.isActive('bold') ? 'is-active' : ''}`}
                    title={t('editor.bold')}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path></svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={`toolbar-btn ${editor.isActive('italic') ? 'is-active' : ''}`}
                    title={t('editor.italic')}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="4" x2="10" y2="4"></line><line x1="14" y1="20" x2="5" y2="20"></line><line x1="15" y1="4" x2="9" y2="20"></line></svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    className={`toolbar-btn ${editor.isActive('strike') ? 'is-active' : ''}`}
                    title={t('editor.strikethrough')}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><path d="M16 6C16 6 14.5 4 12 4C9.5 4 8 6 8 8C8 10 10 12 12 12C14 12 16 14 16 16C16 18 14.5 20 12 20C9.5 20 8 18 8 18"></path></svg>
                </button>
            </div>

            <div className="toolbar-group">
                <button
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className={`toolbar-btn ${editor.isActive('bulletList') ? 'is-active' : ''}`}
                    title={t('editor.bulletList')}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    className={`toolbar-btn ${editor.isActive('orderedList') ? 'is-active' : ''}`}
                    title={t('editor.orderedList')}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="10" y1="6" x2="21" y2="6"></line><line x1="10" y1="12" x2="21" y2="12"></line><line x1="10" y1="18" x2="21" y2="18"></line><path d="M4 6h1v4"></path><path d="M4 10h2"></path><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"></path></svg>
                </button>
            </div>

            <div className="toolbar-group">
                <button
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    className={`toolbar-btn ${editor.isActive('blockquote') ? 'is-active' : ''}`}
                    title={t('editor.quote')}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}
                    className="toolbar-btn"
                    title={t('editor.divider')}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                </button>
            </div>

            <div className="toolbar-group">
                <button
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    className={`toolbar-btn ${editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}`}
                    title={t('editor.alignLeft', 'Align left')}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/></svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                    className={`toolbar-btn ${editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}`}
                    title={t('editor.alignCenter', 'Align center')}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    className={`toolbar-btn ${editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}`}
                    title={t('editor.alignRight', 'Align right')}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="6" y1="18" x2="21" y2="18"/></svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                    className={`toolbar-btn ${editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''}`}
                    title={t('editor.alignJustify', 'Justify')}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                </button>
            </div>

            <div className="toolbar-group" style={{ marginLeft: 'auto', marginRight: 0, borderRight: 'none' }}>
                <button onClick={onToggleRtl} className={`toolbar-btn ${isRtl ? 'is-active' : ''}`} title={t('editor.rtl', 'Toggle RTL (⇌)')}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 4h10"/><path d="M16 4v16"/><path d="M20 4v16"/><path d="M7 15l-4 4 4 4"/></svg>
                </button>
                <button onClick={onToggleFind} className="toolbar-btn" title={t('editor.find', 'Find (⌘F)')}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                </button>
                <button onClick={() => editor.chain().focus().undo().run()} className="toolbar-btn" title={t('editor.undo')}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7v6h6"></path><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"></path></svg>
                </button>
                <button onClick={() => editor.chain().focus().redo().run()} className="toolbar-btn" title={t('editor.redo')}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 7v6h-6"></path><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13"></path></svg>
                </button>
            </div>
        </div>
    );
};

export default RichTextEditor;
